import { GetOfferDetailsQuery, OfferDetailsMxQuery } from 'api';
import { BooleanVariable, CellsType } from 'types';
import { ROLES, currencySymbol, formatters } from 'utils';
import { CurrencyType } from 'enums';
import { Maybe, PhoneNumber } from 'api';
import i18n from 'config/locales';
import {
  parsePhoneNumber,
  Country as PhoneCountryCode,
} from 'react-phone-number-input';

export const getFormattedCurrency = (region: string, amount: number) => {
  const symbol = currencySymbol[region] || '';
  let convertedAmt = '';
  switch (region) {
    case CurrencyType.USD:
    case CurrencyType.EURO:
    case CurrencyType.MXN:
      convertedAmt = amount.toLocaleString('en-US');
      return `${symbol} ${convertedAmt || amount}`;
    case CurrencyType.INR:
      convertedAmt = amount.toLocaleString('en-IN');
      return `${symbol} ${convertedAmt || amount}`;
    default:
      return `${symbol} ${amount}`;
  }
};

export const renderTableHeading = (field?: {
  com_name?: React.ReactNode;
  cust_name?: string | React.ReactNode;
  email?: string | React.ReactNode;
  status?: string | React.ReactNode;
  cpName?: string | React.ReactNode;
  action?: React.ReactNode;
}) => {
  const output = [];
  for (const key in field) {
    output.push({
      id: key,
      label: `raa_table_heading_${key}`,
      minWidth: 180,
      align: 'left',
    });
  }
  return output;
};

export const sanitizePhoneNumberInput = (value?: string): string =>
  value?.replace(/\D/g, '') || '';

/**
 * Strips a leading country calling code that got merged into the national
 * number (e.g. phoneNumber "13073729130" with countryCode "US-1" should be
 * "3073729130"). Uses libphonenumber-js so a national number that legitimately
 * starts with the same digit(s) as the calling code isn't corrupted.
 */
export const sanitizePhoneNumberWithCountryCode = (
  phoneNumber?: string,
  countryCode?: string
): string => {
  const digitsOnly = sanitizePhoneNumberInput(phoneNumber);
  const alpha2 = countryCode?.split('-')[0] as PhoneCountryCode | undefined;
  if (!digitsOnly || !alpha2) {
    return digitsOnly;
  }

  const parsed = parsePhoneNumber(digitsOnly, alpha2);
  return parsed?.isValid() ? parsed.nationalNumber : digitsOnly;
};

export const formatDateInput = (value: string | Date) => {
  if (typeof value !== 'string') {
    return value;
  }
  if (value === '') {
    return value;
  }
  const [day, month, year] = value.indexOf('/')
    ? value.split('/')
    : ['', '', ''];
  return month && day && year
    ? new Date(`${month}/${day}/${year}`)
    : new Date();
};

export default {
  createData: (title: string, subject: string) => {
    return { title, subject };
  },
  dateFormatter: (value?: string | Date) => {
    const date = new Date(formatDateInput(value || ''));
    const leadingZero = (str: string) => {
      return ('0' + str).slice(-2);
    };
    return {
      year: date.getFullYear().toString(),
      month: leadingZero((date.getMonth() + 1).toString()),
      day: leadingZero(date.getDate().toString()),
    };
  },
  getRoles: (
    legalRepresentative: BooleanVariable,
    principleShareholder: BooleanVariable,
    secondaryShareholder: BooleanVariable
  ) => {
    const arr = [];
    if (legalRepresentative) {
      arr.push(ROLES.legalRepresentative);
    }
    if (principleShareholder) {
      arr.push(ROLES.principleShareholder);
    }
    if (secondaryShareholder) {
      arr.push(ROLES.shareholder);
    }
    return arr;
  },

  getFirstName: (fullName: string) => {
    if (fullName) return fullName?.split(' ')[0];
    return '';
  },

  generateOfferDetails: ({
    offerDetails,
  }: GetOfferDetailsQuery): CellsType[] => {
    const facilitySizeTmp =
      parseInt(offerDetails?.offerDetails?.facilitySize || '0') || 0;

    const currency = offerDetails?.currency || 'usd';

    return [
      formatters.createData(
        i18n.t('title_facility_limit'),
        getFormattedCurrency(currency, facilitySizeTmp) || '0'
      ),
      formatters.createData(
        i18n.t('title_advance_rate'),
        i18n.t('title_advance_rate_placeholder', {
          advanceRate: parseInt(offerDetails?.offerDetails?.advanceRate || '0'),
        })
      ),
      formatters.createData(
        i18n.t('title_maximum_credit_tenor'),
        offerDetails?.offerDetails?.maxCreditTenor || '0'
      ),
      formatters.createData(
        i18n.t('title_discount_rate'),
        i18n.t('title_interest_rate_placeholder', {
          interestRate: offerDetails?.offerDetails?.interestRate || 0,
        })
      ),
      formatters.createData(
        i18n.t('title_establishment_fee'),
        i18n.t('title_establishment_fee_placeholder', {
          establishmentFee: offerDetails?.offerDetails?.establishimentFee || 0,
        })
      ),
    ];
  },

  generateMXOfferDetails: (
    data: OfferDetailsMxQuery | undefined
  ): CellsType[] => {
    const offer = data?.offer;
    const facilitySizeTmp = offer?.offerDetails?.facilitySize || 0;

    const currency = CurrencyType.USD;

    return [
      formatters.createData(
        i18n.t('title_facility_size'),
        getFormattedCurrency(currency, facilitySizeTmp) || '0'
      ),
      formatters.createData(
        i18n.t('title_advance_rate'),
        i18n.t('title_advance_rate_placeholder', {
          advanceRate: offer?.offerDetails?.advanceRate || 0,
        })
      ),
      formatters.createData(
        i18n.t('title_maximum_credit_tenor'),
        i18n.t('title_maximum_credit_tenor_placeholder', {
          days: offer?.offerDetails?.maximumCreditTenor || 0,
        })
      ),
      formatters.createData(
        i18n.t('title_interest_rate'),
        i18n.t('title_interest_rate_placeholder', {
          interestRate: offer?.offerDetails?.interestRate || 0,
        })
      ),
      formatters.createData(
        i18n.t('title_establishment_fee'),
        i18n.t('title_establishment_fee_placeholder', {
          establishmentFee: offer?.offerDetails?.establishmentFee || 0,
        })
      ),
    ];
  },

  generateEmptyOffer: () => {
    return [
      formatters.createData(i18n.t('title_facility_limit'), '-'),
      formatters.createData(i18n.t('title_advance_rate'), '-'),
      formatters.createData(i18n.t('title_maximum_credit_tenor'), '-'),
      formatters.createData(i18n.t('title_discount_rate'), '-'),
      formatters.createData(i18n.t('title_establishment_fee'), '-'),
    ];
  },

  generateSelectOptions: (values: string[]) => {
    return values
      .map((val) => ({ value: val, label: val }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  },

  generateCountryOptions: (options?: null | string[]) => {
    if (!options) {
      return [];
    }
    return options
      .map((optionValue: string) => {
        return {
          value: optionValue,
          label: optionValue,
        };
      })
      .sort((a: { label: string }, b: { label: string }) =>
        a.label > b.label ? 1 : -1
      );
  },

  generateCountryOptionsWithId: (options?: null | any[]) => {
    if (!options) {
      return [];
    }
    return Object.keys(options)
      .map((id: string) => {
        return {
          id: id,
          value: id,
          label: options[id],
        };
      })
      .sort((a: { label: string }, b: { label: string }) =>
        a.label > b.label ? 1 : -1
      );
  },

  generateCommonOptionsWithSort: (options: object) => {
    if (!options) {
      return [];
    }
    return Object.keys(options)
      .map((key) => {
        return {
          value: key,
          label: options[key],
        };
      })
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  },

  generateCommonOptions: (options: object) => {
    if (!options) {
      return [];
    }
    return Object.keys(options).map((key) => {
      return {
        value: key,
        label: options[key],
      };
    });
  },

  generateProductCategoryOptions: (options: object) => {
    if (!options) {
      return [];
    }
    return Object.keys(options)
      .map((key) => {
        return {
          value: key,
          label: options[key],
        };
      })
      .sort((a, b) => {
        if (a.label === b.label) return 0;
        if (a.label === 'Other') return 1;
        if (b.label === 'Other') return -1;
        return a.label > b.label ? 1 : -1;
      });
  },

  maskFields(type: string, value?: string) {
    const generateAsterisks = (length: number) => {
      if (length && length > 0) {
        return Array(length || 0)
          .fill('*')
          .join('');
      }
      return '';
    };

    if (value) {
      switch (type) {
        case 'phone':
          value = `${generateAsterisks(value?.length || 0 - 3)}${
            value?.slice(-3) || '***'
          }`;
          break;
        case 'email':
          if (value?.indexOf('@') > 0) {
            const temp = value?.split('@');
            const username =
              (temp?.[0]?.slice(0, 3) || '***') +
              generateAsterisks(temp?.[0]?.length || 0 - 3);
            const domain =
              generateAsterisks(temp?.[1]?.length - 4) +
              temp?.[1]?.slice(temp?.[1]?.indexOf('.'));
            value = `${username}@${domain}`;
          } else {
            value = '*****@***.com';
          }
          break;
        case 'ssn': {
          value = '******' + value.substring(6);
          break;
        }
        default:
      }
    }
    return value;
  },

  sanitizeFields(data: object) {
    const dataTemp: any = {
      ...data,
    };
    if (dataTemp) {
      Object.keys(dataTemp).forEach((key) => {
        if (dataTemp[key] === null) {
          dataTemp[key] = undefined;
        }
      });
    }
    // specially for buyer form
    if (dataTemp?.contactPhoneNumber?.phoneNumber === null) {
      dataTemp.contactPhoneNumber = {
        countryCode: 'IN-91',
        phoneNumber: undefined,
      };
    }
    return dataTemp;
  },

  displayPhoneNumber: (contact?: Maybe<PhoneNumber>): string => {
    if (contact?.countryCode) {
      const code = contact?.countryCode?.split('-')?.[1] || '';
      const number =
        contact?.phoneNumber?.substring(0, 3) +
        ' ' +
        contact?.phoneNumber?.substring(3, 6) +
        ' ' +
        contact?.phoneNumber?.substring(6);
      return `${code && '+'}${code}${code && ' '}${number}`;
    }
    return '';
  },

  formatToUSDate(date: string) {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  },
};

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export function humanFileSize(bytes: number, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(dp) + ' ' + units[u];
}

// pdfjs-dist actually decrypts PDFs (RC4 and AES, all revisions) rather than
// just detecting an /Encrypt dictionary, so it correctly distinguishes a real
// user-password requirement from owner-only encryption (very common on
// bank-issued statements: encrypted for print/copy restrictions, but opens
// with no prompt at all). Loading via getDocument() only throws
// PasswordException when a password is genuinely required to open the file.
//
// Worker strategy: run the worker code ON THE MAIN THREAD rather than as a
// separate web-worker asset. The `new URL('.../pdf.worker.min.js',
// import.meta.url)` approach worked in the CRA dev server but broke in the
// production build on staging — webpack emits the worker chunk with a bare
// CommonJS `require` (from pdf.worker.entry.js) that isn't defined in the
// worker scope, so it threw "require is not defined" / "Setting up fake
// worker failed", getDocument() then rejected with a non-PasswordException
// error, and password-protected files slipped through as a false negative.
// Importing the worker module here (a normal module import webpack fully
// resolves) registers globalThis.pdfjsWorker.WorkerMessageHandler, which
// pdfjs picks up as its main-thread handler (see PDFWorker
// _mainThreadWorkerMessageHandler) — no separate worker file to fetch/bundle,
// so it works identically in dev and prod. Password detection is a one-off
// per upload, so main-thread execution is perfectly fine perf-wise.
let pdfWorkerConfigured = false;
const ensurePdfWorkerConfigured = async () => {
  if (pdfWorkerConfigured) return;
  const pdfjsLib = await import('pdfjs-dist');
  const pdfWorker = await import('pdfjs-dist/build/pdf.worker.js');
  (globalThis as unknown as { pdfjsWorker?: unknown }).pdfjsWorker =
    (pdfWorker as { default?: unknown }).default ?? pdfWorker;
  // Empty workerSrc + a registered main-thread handler makes pdfjs skip the
  // web-worker/loadScript path entirely and run inline.
  pdfjsLib.GlobalWorkerOptions.workerSrc = '';
  pdfWorkerConfigured = true;
};

/**
 * Returns true when a PDF genuinely requires a password to open (i.e. the
 * user would see a password prompt). A PDF encrypted only for owner-side
 * permission restrictions (empty user password) resolves to false, since it
 * opens with no prompt at all. Resolves to false on any non-password read
 * error too, so callers always get a boolean and can let the BE do a
 * secondary rejection if the file turns out to be unreadable for some other
 * reason.
 */
export const isPDFPasswordProtected = async (file: File): Promise<boolean> => {
  try {
    await ensurePdfWorkerConfigured();
    const pdfjsLib = await import('pdfjs-dist');
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const doc = await loadingTask.promise;
    await doc.destroy();
    return false;
  } catch (err) {
    const isPasswordException =
      (err as { name?: string })?.name === 'PasswordException';
    if (!isPasswordException) {
      // TEMP DEBUG: pinpointing why a genuinely password-protected file was
      // reported as not protected — if the worker fails to load (or any
      // other read error occurs), this catch previously swallowed it and
      // silently returned false, letting the file through. Logging here so
      // the real error is visible; remove once root-caused.
      console.error('[isPDFPasswordProtected] non-password error:', err);
    }
    return isPasswordException;
  }
};
